import styled, { css, keyframes } from 'styled-components';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { colors } from 'constants/colors';
import { CheckIcon } from '../icons/CheckIcon';
import { CrossIcon } from '../icons/CrossIcon';
import { StyledDropDownWrapper } from '../DropDown/DropDownStyles';
import arrowDropDownImage from '../../assets/images/icons/arrow_down.svg';
import { desktopQuery } from 'utils/breakpoints';

export const maxDropDownHeight = 400;
export const PRICE_ANIMATION_TIME = 200;
export const PRICE_ANIMATION_CLASS = 'price-animation';

export const StyledSpecialHeader = styled.div`
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  pointer-events: none;
  font-weight: bold;
  font-size: 20px;
  text-shadow: 0 3px 3px #00000040;
`;

export const StyledPlan = styled.div<{ isHighlighted?: Boolean; mobileOnly?: Boolean; special: string }>`
  min-width: 242px;
  max-width: 350px;
  flex-basis: 350px;
  flex-shrink: 1;
  display: flex;
  flex-direction: column;
  margin: 24px auto 16px;
  padding: 0 4px 4px;
  border-radius: 16px;
  
  ${StyledSpecialHeader} {
    transform: translateY(36px);
    max-height: 0;
  }

  ${({ special }) => Boolean(special) && css`
    transition: padding 0.3s ease-out, margin-top 0.3s ease-out;
    padding: 0 4px 4px;
    background: linear-gradient(to right, #62AC5E, #6FCC69);
    
    & > ${StyledSpecialHeader} {
      transition: max-height 0.3s ease-out, transform 0.3s ease-out, opacity 0.3s ease-out;
      transform: translateY(0);
      max-height: 36px;
    }
  `};
  
  
  ${({ isHighlighted }) =>
    isHighlighted &&
    css`
      box-shadow: 0 0 8px #f28a21;
    `
  };

  ${desktopQuery(css<{ isSelected?: Boolean; special?: string }>`
    border: none;
    margin-top: 60px;
    
    ${({ special }) => Boolean(special) && css`
      margin-top: 24px;
    `};

    ${StyledDropDownWrapper} {
      max-height: ${maxDropDownHeight}px;
    }

    ${(props) =>
      props.isSelected &&
      css`
        box-shadow: 0 0 48px ${colors.grey};
        background-color: ${colors.white};
      `};
  `)};

  ${({ mobileOnly }) =>
    mobileOnly &&
    desktopQuery(css`
      display: none;
    `)};
`;

export const StyledPlanBody = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  
  padding: 16px 16px 30px;
  border-radius: 16px;
  box-shadow: 0 0 8px #00000044;
  background-color: white;
  z-index: 1;
`;

export const StyledPlanName = styled.div`
  margin-top: 14px;
  font-size: 30px;
  font-weight: bold;
  color: ${colors.yellow};

  @media (max-width: 999px) {
    font-size: 30px;
    display: inline-block;
  }
`;

export const StyledPrice = styled.div<{ isLoading?: Boolean }>`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  width: 100%;
  height: 100%;

  ${({ isLoading }) =>
    isLoading &&
    css`
      animation: ${priceLoadingAnimation};
      animation-duration: 0.5s;
      animation-iteration-count: infinite;
      animation-timing-function: linear;
      animation-direction: alternate-reverse;

      color: ${colors.darkGray2};
    `};
`;

export const StyledCSSTransition = styled(CSSTransition)`
  transition: ${PRICE_ANIMATION_TIME}ms;
  display: flex;
  justify-content: center;

  &.${PRICE_ANIMATION_CLASS}-enter {
    opacity: 0;
  }

  &.${PRICE_ANIMATION_CLASS}-enter-active {
    opacity: 1;
  }

  &.${PRICE_ANIMATION_CLASS}-exit {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    opacity: 1;
  }

  &.${PRICE_ANIMATION_CLASS}-exit-active {
    opacity: 0;
  }
`;

export const StyledTransitionGroup = styled(TransitionGroup)`
  position: relative;
  width: 100%;
`;

export const StyledPriceWrapper = styled.div<{ isDisabled?: Boolean; crossedPrice?: Boolean }>`
  position: relative;
  display: flex;
  justify-content: center;
  font-size: 42px;
  font-weight: bold;
  line-height: 1;
  color: ${(props) => (props.isDisabled ? colors.lightGrey : colors.black)};
  ${({ crossedPrice }) =>
    crossedPrice &&
    css`
      color: ${colors.black100};
      transform: translateY(4px) scale(0.7);
      margin: 0;
    `};
`;

const priceLoadingAnimation = keyframes`
  0% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
`;

export const StyledRestPrice = styled.div`
  font-size: 18px;
  transform: translateY(4px);
  display: flex;
  height: 100%;
`;

export const StyledPlanButton = styled.div<{ isDisabled?: Boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  width: 100%;
  text-align: center;
  height: 64px;
  border-radius: 8px;
  font-weight: bold;
  font-size: 24px;
  cursor: pointer;
  transition: background-color 0.2s, filter 0.2s, transform 0.2s, box-shadow 0.2s;
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.25);
  background-color: ${colors.orange};
  color: ${colors.white};
  user-select: none;

  &:hover {
    filter: brightness(1.3);
    box-shadow: 0 6px 8px rgba(0, 0, 0, 0.25);
    transform: scale(1.03);
  }

  ${(props) =>
    props.isDisabled &&
    css`
      &,
      &:hover {
        background-color: ${colors.lightGrey};
      }

      border: none;
      box-shadow: none;
      cursor: default;
      pointer-events: none;
      user-select: none;
      color: ${colors.white};
    `};
`;
const StyledCheckedOption = css`
  margin-left: 0;
`;
export const StyledOption = styled.div<{ checked?: Boolean }>`
  text-align: left;
  margin-top: 8px;
  display: flex;
  justify-content: flex-start;
  min-height: 26px;
  line-height: 26px;
  white-space: pre-line;
  color: ${colors.black};

  ${(props) => props.checked && StyledCheckedOption}
  
  sup {
    top: 0;
    color: ${colors.orange};
  }
`;

export const StyledOptionList = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 60px;

  & > * {
    position: relative;

    :before {
      content: '■';
      display: flex;
      align-items: center;
      position: absolute;
      font-size: 20px;
      line-height: 24px;
      padding-right: 8px;
      right: 100%;
    }
  }
`;

export const StyledDescription = styled(StyledOption)`
  margin-top: 12px;
  font-weight: bold;
  color: ${colors.grey};
  justify-content: center;

  @media (max-width: 999px) {
    height: auto;
    line-height: 22px;
  }
`;
export const StyledOptionsWrapper = styled.div`
  margin: 24px auto 0;
`;
export const StyledCheckIcon = styled(CheckIcon)`
  height: 12px;
  width: 12px;
  margin: 6px 12px 10px 12px;
  display: inline-block;
  fill: ${colors.green};
  flex-shrink: 0;
`;

export const StyledCrossIcon = styled(CrossIcon)`
  height: 12px;
  width: 12px;
  margin: 6px 12px 10px 18px;
  display: inline-block;
  fill: ${colors.darkRed};
  flex-shrink: 0;
`;

export const StyledDropDownArrow = styled.div<{ isExpanded?: Boolean }>`
  width: 20px;
  height: 10px;
  margin: 6px 0 6px 6px;
  background: url('${arrowDropDownImage}') no-repeat;
  background-size: contain;
  float: right;
  transition: transform 0.5s;
  cursor: pointer;

  ${(props) => props.isExpanded && 'transform: rotateZ(-180deg)'};

  @media (min-width: 1000px) {
    display: none;
  }
`;

export const StyledDiagonalLine = styled.div`
  position: absolute;
  height: 3px;
  background-color: ${colors.black100};
  transform: rotate(9deg);
  top: 50%;
  width: 70%;
  border-radius: 3px;

  @media (max-width: 999px) {
    width: 35%;
    bottom: 13px;
  }
`;

export const StyledPerMonth = styled.span`
  font-size: 32px;
  font-weight: normal;
`;

export const StyledPricesWrapper = styled.div`
  min-height: 120px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;
